import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { EmailTemplate } from '@rossum/api-client/emailTemplates';
import { Message } from '@rossum/api-client/shared';
import { IconAlertOctagon, IconCircleCheck } from '@rossum/ui/icons/tabler';
import { Box, SvgIcon, Tooltip, Typography } from '@rossum/ui/material';
import HTMLMessage from '../../../components/DatapointMessage/HTMLMessage';
import { ValidationErrorsPerQueue } from '../hooks/useValidateRuleAgainstQueues';
import { categorizeValidationMessages } from '../utils/categorizeValidationMessages';

type ValidationStatusCellProps = {
  queueValidation: ValidationErrorsPerQueue | null | undefined;
  unavailableEmailTemplates: Pick<EmailTemplate, 'name'>[];
};

export const ValidationStatusCell = ({
  queueValidation,
  unavailableEmailTemplates,
}: ValidationStatusCellProps) => {
  const intl = useIntl();

  if (!queueValidation) {
    return null;
  }

  const emailTemplateWarning: Message | null =
    unavailableEmailTemplates.length > 0
      ? {
          type: 'warning',
          content: intl.formatMessage(
            {
              id: 'features.ruleManager.queues.validation.emailTemplateWarning',
            },
            {
              count: unavailableEmailTemplates.length,
              templates: unavailableEmailTemplates
                .map(t => `"${t.name}"`)
                .join(', '),
            }
          ),
        }
      : null;

  const {
    conditionErrors,
    actionErrors,
    otherErrors: apiOtherErrors,
  } = categorizeValidationMessages(queueValidation.errors);
  const otherErrors = emailTemplateWarning
    ? [...apiOtherErrors, emailTemplateWarning]
    : apiOtherErrors;
  const hasIssues =
    queueValidation.errors.length > 0 || emailTemplateWarning !== null;

  if (!hasIssues) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
          width: '100%',
          height: '100%',
        }}
      >
        <SvgIcon fontSize="large" sx={{ color: 'success.main' }}>
          <IconCircleCheck />
        </SvgIcon>
      </Box>
    );
  }

  const tooltipContent = (
    <Box sx={{ p: 1, textAlign: 'left' }}>
      {conditionErrors.length > 0 && (
        <Box sx={{ mb: actionErrors.length > 0 ? 1.5 : 0 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {intl.formatMessage({
              id: 'features.ruleManager.queues.validation.conditionNotValid',
            })}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, textAlign: 'left' }}>
            {conditionErrors.map(msg => (
              <Typography component="li" variant="body2" key={msg.content}>
                {msg.content}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      {actionErrors.length > 0 && (
        <Box sx={{ mb: otherErrors.length > 0 ? 1.5 : 0 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {intl.formatMessage({
              id: 'features.ruleManager.queues.validation.actionNotValid',
            })}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, textAlign: 'left' }}>
            {actionErrors.map(msg => (
              <Typography component="li" variant="body2" key={msg.content}>
                {msg.content}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      {otherErrors.length > 0 && (
        <Box>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {intl.formatMessage({
              id: 'features.ruleManager.queues.validation.otherErrors',
            })}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, textAlign: 'left' }}>
            {otherErrors.map(msg => (
              <Typography component="li" variant="body2" key={msg.content}>
                <HTMLMessage content={R.truncate(msg.content, 200)} />
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="right">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
          width: '100%',
          height: '100%',
        }}
      >
        <SvgIcon fontSize="large" sx={{ color: 'error.main' }}>
          <IconAlertOctagon />
        </SvgIcon>
        <Typography variant="body2">
          {[
            conditionErrors.length > 0 &&
              intl.formatMessage({
                id: 'features.ruleManager.queues.validation.conditionNotValid',
              }),
            actionErrors.length > 0 &&
              intl.formatMessage({
                id: 'features.ruleManager.queues.validation.actionNotValid',
              }),
            otherErrors.length > 0 &&
              intl.formatMessage({
                id: 'features.ruleManager.queues.validation.otherErrors',
              }),
          ]
            .filter(Boolean)
            .join(', ')}
        </Typography>
      </Box>
    </Tooltip>
  );
};
